import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  StepType,
  RegistrationStepQuery,
  LoggingService,
  ErrorStateService,
  RegistrationStepService,
  MemberQuery,
  MemberDetails,
  ChildQuery,
  ChildService,
  AuthService,
  RegistrationStep,
  RegistrationStepSettings,
  toNumber
} from '@fgb/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { ModelFormHelperService } from '@fgb/portal-component-library/src/lib/shared/forms/model-meta-data-form';
import { Observable, Subscription, combineLatest, iif, of } from 'rxjs';
import { pluck } from 'rxjs/operators';

@Component({
  selector: 'fgbcl-registration-add-children',
  templateUrl: './registration-add-children.component.html',
  styleUrls: ['./registration-add-children.component.scss'],
})
export class RegistrationAddChildrenComponent implements OnInit, OnDestroy {
  readonly stepType = StepType.AddChild;
  today = new Date();
  currentUser: MemberDetails | undefined;
  childrenForm: UntypedFormGroup;
  stepData: RegistrationStep | undefined;
  // In Angular v16 there is a feature that binds inputs to component using the route and pass the Input property in the data object in the route.
  // https://itnext.io/bind-route-info-to-component-inputs-new-router-feature-1d747e559dc4
  @Input() minAge: number;
  @Input() maxAge: number;
  @Input() childLimit: number;
  @Input() childModel = [];
  private _subscription: Subscription;
  isLoaded$: Observable<boolean>;

  constructor(
    private registrationStepQuery: RegistrationStepQuery,
    private registrationStepService: RegistrationStepService,
    private logService: LoggingService,
    public errorService: ErrorStateService,
    private formBuilder: UntypedFormBuilder,
    private memberQuery: MemberQuery,
    private childQuery: ChildQuery,
    private childService: ChildService,
    private modelForm: ModelFormHelperService,
    private authService: AuthService
  ) {
  }

  ngOnInit() {
    this.stepData = this.registrationStepQuery.getStep(this.stepType);

    if (!this.stepData) {
      this.logService.warning('No step data found in the store for step', this.stepType);
      return;
    }
    const isAuthenticated = this.authService.isAuthenticated();

    if( isAuthenticated ){
      this.childService.fetchChildCountLimit().toPromise();
      this.childService.fetchChildren().toPromise();
    }

    this.isLoaded$ = iif(() => isAuthenticated, this.childQuery.isLoaded$, of(true));
    this._initSettings();

    this._subscription = combineLatest([ 
      this.memberQuery.selectMemberDetails(), 
      iif(() => isAuthenticated, this.childQuery.getChildrenInfo(), of({ data: [], limit: this.childLimit })) 
    ])
    .subscribe(([ md, childrenInfo ]) => {
      this.currentUser = md;
      this.childLimit = childrenInfo.limit - childrenInfo?.data.length;
    }); 

    // Initialize form
    this._initializeForm();
  }

  ngOnDestroy(): void {
    if( this._subscription ){
      this._subscription.unsubscribe();
    }
  }

  /**
   * Fetch model for the step and initialize the form
   */
  private _initializeForm(): void{
    this.registrationStepService
    .fetchRegistrationStepForCurrentWizard(this.stepType, true)
      .pipe(pluck('Data'))
      .toPromise()
      .then(models => {
        this.childModel = models;
        this.childrenForm = this._generateChildrenForm(this.stepData?.Data);
      });
  }

  /**
   * Initialize settings for the step
   */
  private _initSettings(): void{
    const settings = this.registrationStepQuery.getStepSettings(this.stepType) as RegistrationStepSettings;
    // If settings in the step is zero or not a number, use the default value or from the route data
    this.childLimit = toNumber(settings?.ChildLimit) || this.childLimit;
    this.minAge = toNumber(settings?.MinAge) || this.minAge;
    this.maxAge = toNumber(settings?.MaxAge) || this.maxAge;
  }

  /**
   * Generate children form
   */
  private _generateChildrenForm(data: any[] = []): UntypedFormGroup{
    return this.formBuilder.group({
      children: this.formBuilder.array(data?.length > 0 ? 
          data.map((child: any) => this.generateChildForm(child)) : 
          [ this.generateChildForm() ]
        )
    });
  }

  submitStep() { 
    this.errorService.clearErrors();
    this.childrenForm.markAllAsTouched();
    
    if( !this.childrenForm.valid ){
      this.logService.info('Form is invalid', this.childrenForm);
      return;
    }

    this.logService.info('Attempting step submission.');
    const stepData = this.childrenForm.value.children.map((child: any) => this.modelForm.transformData(child));

    this.logService.info('Submitting step data:', stepData);
    this.registrationStepService.setStepCompleteStatus(StepType.MembershipOption, false);
    this.registrationStepService.submitRegistrationStepData(this.stepType, stepData).toPromise()
      .then(() => {
        this.childService.disableChildStoreCache();
      });
  }

  /**
   * Generate child form dynamically from the the model
   */
  generateChildForm(child?: any ){
    return this.modelForm.buildForm(this.childModel, child);
  }
  
  /**
   * Add a new child form to the form array
   */
  addChild(){
    const arr = this.childrenForm.get(`children`) as UntypedFormArray;
    arr.push(this.generateChildForm());
  }

  /**
   * Remove child form at the specified index
   */
  removeChildAt(childIndex: number): void{
    const arr = this.childrenForm.get(`children`) as UntypedFormArray;
    arr.removeAt(childIndex);
  }

  /**
   * Clear child form at the specified index
   */
  clearChildAt(childIndex: number): void{
    const arr = this.childrenForm.get(`children`) as UntypedFormArray;
    arr.at(childIndex).reset();
  }
}
